import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import type { Member } from '@/shared/types/member';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import { DeleteAPIKeyCell } from './DeleteAPIKeyCell';
import { apiKeyTypeLabelMapper } from './helper';
import { Credential } from '../../types';

interface ColumnsProps {
  onDelete: (credential: Credential, onSettled: () => void) => void;
  getMember: (userId: string) => Member | undefined;
  canDelete: boolean;
}

export const createColumns = ({
  onDelete,
  getMember,
  canDelete,
}: ColumnsProps): ColumnDef<Credential>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX', className: 'pl-4' },
    header: ({ column }) => <SortableHeader column={column}>{t('common.name')}</SortableHeader>,
    cell: ({ row }) => <span className="truncate font-medium">{row.original.name}</span>,
  },
  {
    id: 'type',
    accessorFn: (row) => apiKeyTypeLabelMapper(row.type, t),
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    header: ({ column }) => <SortableHeader column={column}>{t('common.type')}</SortableHeader>,
    cell: ({ row }) => (
      <Badge type="secondary" flavour="neutral" rounded={false}>
        {apiKeyTypeLabelMapper(row.original.type, t)}
      </Badge>
    ),
  },
  {
    id: 'created_by',
    accessorFn: (row) => {
      const member = row.created_by ? getMember(row.created_by) : undefined;
      return member?.name || '';
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.created_by')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const createdBy = row.original.created_by;
      if (!createdBy) {
        return <span className="text-foreground-muted">-</span>;
      }
      const member = getMember(createdBy);
      const fallback = member?.name?.charAt(0) || '?';
      return (
        <div className="flex items-center gap-2">
          <Avatar size="xs">
            <AvatarImage src={member?.avatar || ''} alt={member?.name || ''} />
            <AvatarFallback>{fallback}</AvatarFallback>
          </Avatar>
          <span className="truncate">{member?.name || '-'}</span>
        </div>
      );
    },
  },
  {
    id: 'created',
    accessorKey: 'created',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    header: ({ column }) => <SortableHeader column={column}>{t('common.created')}</SortableHeader>,
    cell: ({ row }) => {
      const created = row.original.created;
      return created ? (
        <TimestampTooltip timestamp={created}>{timeFromNow(created)}</TimestampTooltip>
      ) : (
        '-'
      );
    },
  },
  {
    id: 'last_used_at',
    accessorKey: 'last_used_at',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.last_used')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const lastUsed = row.original.last_used_at;
      return lastUsed ? (
        <TimestampTooltip timestamp={lastUsed}>{timeFromNow(lastUsed)}</TimestampTooltip>
      ) : (
        '-'
      );
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
    cell: ({ row }) => (
      <DeleteAPIKeyCell credential={row.original} canDelete={canDelete} onDelete={onDelete} />
    ),
  },
];
